import { Dayjs } from 'dayjs'
import stringify from 'fast-safe-stringify'
import { LexicalEditor } from 'lexical'

import { getOnlyDate, isSameOrAfter, isSameOrBefore } from '~/astra'
import { Schemas } from '~/clients/api-client'
import { importCommentsThreads } from '~/store/di-entity.api.generated'
import { LocationDateSelection, ScheduleDateSelection, SidebarSelectionType } from '~/store/slices/selectionDrawerSlice'
import { UserSlice } from '~/store/slices/userSlice'
import { ThreadRefType } from '~/storm'

import { PlanningPeriodSelection, ThreadSelection } from '../RichTextEditing/useRichTextThread'
import { getApiMentions } from './lexical/utils'

type AnyDate = Dayjs | string

export type ThreadsQueryFilter = Parameters<typeof importCommentsThreads>[0]

export const selectionTypeToThreadRefType = {
    scheduleDate: 'schedule_date_thread_ref',
    locationDate: 'location_schedule_thread_ref',
    planningPeriod: 'planning_period_thread_ref',
} satisfies Record<SidebarSelectionType | 'planningPeriod', ThreadRefType>

export const isScheduleDateSelection = (selection: ThreadSelection): selection is ScheduleDateSelection => selection.type === 'scheduleDate'
export const isLocationDateSelection = (selection: ThreadSelection): selection is LocationDateSelection => selection.type === 'locationDate'
export const isPlanningPeriodSelection = (selection: ThreadSelection): selection is PlanningPeriodSelection => selection.type === 'planningPeriod'
export const selectionToThreadsFilter = (selection: ThreadSelection) => {
    if (isPlanningPeriodSelection(selection)) {
        return {
            'ref.type': selectionTypeToThreadRefType[selection.type],
            'ref.planning_period_id': selection.planningPeriodId,
        }
    }
    const refFilter = {
        'ref.type': selectionTypeToThreadRefType[selection.type],
        'ref.schedule_date': getOnlyDate(selection.date),
    }

    if (isScheduleDateSelection(selection)) {
        return refFilter
    }

    return {
        ...refFilter,
        'ref.location_id': selection.locationId,
    }
}

export const dateRangeToThreadsFilter = (startDate: AnyDate | undefined, endDate: AnyDate | undefined) =>
    ({
        'ref.schedule_date:gte': startDate ? getOnlyDate(startDate) : undefined,
        'ref.schedule_date:lte': endDate ? getOnlyDate(endDate) : undefined,
    }) satisfies ThreadsQueryFilter

export const isDateInThreadsFilterRange = (date: AnyDate, threadsFilter?: ThreadsQueryFilter) => {
    if (!threadsFilter) {
        return false
    }
    const startDate = threadsFilter['ref.schedule_date:gte']
    const endDate = threadsFilter['ref.schedule_date:lte']

    if (!startDate || !endDate) {
        return false
    }

    return isSameOrBefore(date, endDate) && isSameOrAfter(date, startDate)
}

export type ThreadContentPayload = Schemas['CommentsThreadCommentCreateRequest']

export const lexicalEditorToContentPayload = (editor: LexicalEditor) => ({
    raw_content: stringify(editor.getEditorState().toJSON()),
    mentions: getApiMentions(editor),
})

type OwnableEntity = {
    updated_by?: string | null
    created_by?: string | null
}
/**
 * We store the display name for user in created/updated_by fields.
 * However what is stored in these fields vary based on the IDP.
 * This is a loose check, and a workaround
 * because we can't guarantee other than string checking...
 * @param thread F
 * @param user
 * @returns
 */
export const isLikelyOwner = (entity: OwnableEntity, user: Pick<UserSlice['user'], 'display_name' | 'username'>): boolean =>
    entity.updated_by === user.display_name || (!!entity.created_by && [user.display_name, user.username].includes(entity.created_by))
